import { isArray } from "lodash";
import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import { AsyncResult, PaginatedDataResult, useMappedStore, usePaginatedStore, useStoredData } from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { Carousel, CarouselStatus } from "./carousel";

export function useCarousel(id: string, slug?: string): AsyncResult<Carousel> {
	const { carouselService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(slug ?? id, slug ? carouselService.slugCarouselStore : carouselService.carouselStore, [
		currentProfile?.id,
		slug,
	]);
}

export function useSuggestedCarousels(
	count: number,
	cat: Category | Category[] | undefined,
	language: ContentLanguage
): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();
	const store = useMemo(
		() =>
			new StorePresentation(carouselService.carouselStore, () =>
				// TODO : Should call here suggest() method of carouselService instead of search().
				// Calling search() in offline mode does not return data to suggest to user.
				// See useSuggestedArticles() or useSuggestedCourses() for example.
				carouselService.search({ page: 0, size: 10 }, "", cat ? (isArray(cat) ? cat : [cat]) : [], [])
			),
		[count, cat, language]
	);
	return usePaginatedStore(store, [count]);
}

export function useFavoriteCarousels(userId: string): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();
	return usePaginatedStore(carouselService.favoriteCarouselsPresentation, [userId]);
}

export function usePersonCarousels(personId: string, organization?: boolean): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();
	return useMappedStore(
		personId,
		organization ? carouselService.organizationCarouselsStore : carouselService.profileCarouselsStore,
		[personId, organization]
	);
}

export function useMyCarousels(status: CarouselStatus | "ALL"): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();
	return useMappedStore(status, carouselService.myCarouselsStore, [status]);
}
export function useSuggestedCarouselsForCarouselPage(count: number, carouselId: string): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();

	const store = useMemo(
		() =>
			new StorePresentation(carouselService.carouselStore, () => carouselService.suggestByCarousel(count, carouselId)),
		[count, carouselId]
	);
	return usePaginatedStore(store, [count]);
}

// get suggestion by same author
export function useSuggestedCarouselsBySameAuthor(count: number, carouselId: string): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();

	const store = useMemo(
		() =>
			new StorePresentation(carouselService.carouselStore, () =>
				carouselService.suggestCarouselBySameAuthor(count, carouselId)
			),
		[count, carouselId]
	);
	return usePaginatedStore(store, [count]);
}

// get suggestion by same category
export function useSuggestedCarouselsBySameCategory(count: number, carouselId: string): PaginatedDataResult<Carousel> {
	const { carouselService } = useServices();

	const store = useMemo(
		() =>
			new StorePresentation(carouselService.carouselStore, () =>
				carouselService.suggestCarouselBySameCategory(count, carouselId)
			),
		[count, carouselId]
	);
	return usePaginatedStore(store, [count]);
}
